import { superAdminApi } from "./authService";
import { OwnerUser } from "./tenantService";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../types/api";

const TRIAL_QUEUE_RESOURCE = "/super-admin/tenants/trial-queue";
const ENRICHMENT_RESOURCE = "/super-admin/tenants";
const CONCIERGE_RESOURCE = "/super-admin/concierge";

export interface TrialQueueItem {
  id: number;
  name: string;
  document: string;
  owner_user: OwnerUser | null;
  professionals_count: number;
  units_count: number;
  lead_score: number;
  trial_started_at: string | null;
  concierge_gate_passed: boolean;
  concierge_booked_at: string | null;
  aha_completed: boolean;
  aha_type: string | null;
  badges: string[];
  subscription: {
    status: string;
    plan: { id: number; name: string } | null;
  } | null;
}

export interface EnrichmentSummary {
  headline: string;
  estimated_size: string;
  priority: string;
  highlights: string[];
  model: string;
  generated_at: string;
}

export interface EnrichmentSource {
  type: string;
  url: string;
  checked_at: string;
}

export interface TenantEnrichment {
  id: number;
  tenant_id: number;
  lead_score: number;
  summary: EnrichmentSummary;
  signals: Record<string, boolean>;
  sources: EnrichmentSource[];
  computed_at: string;
}

export interface ConciergeSlot {
  id: number;
  starts_at: string;
  ends_at: string;
  duration_minutes: number;
  super_admin_id: number | null;
  is_available: boolean;
}

export interface ConciergeAppointmentItem {
  id: number;
  tenant_id: number;
  tenant_name: string | null;
  status: string;
  notes: string | null;
  booked_at: string | null;
  completed_at: string | null;
  booked_by: OwnerUser | null;
  slot: {
    id: number;
    starts_at: string;
    ends_at: string;
    duration_minutes: number;
  } | null;
}

export interface PublishConciergeSlotPayload {
  starts_at: string;
  ends_at?: string;
  duration_minutes?: number;
  super_admin_id?: number;
}

export interface CompleteConciergeBookingPayload {
  notes?: string;
  status?: "completed" | "no_show";
}

export const getTrialQueue = async (
  params?: PaginationParams
): Promise<PaginatedApiResponse<TrialQueueItem>> => {
  const response = await superAdminApi.get<PaginatedApiResponse<TrialQueueItem>>(
    TRIAL_QUEUE_RESOURCE,
    { params }
  );

  return response.data;
};

export const getEnrichment = async (
  tenantId: number
): Promise<ApiResponse<TenantEnrichment>> => {
  const response = await superAdminApi.get<ApiResponse<TenantEnrichment>>(
    `${ENRICHMENT_RESOURCE}/${tenantId}/enrichment`
  );

  return response.data;
};

export const getConciergeSlots = async (): Promise<ApiResponse<ConciergeSlot[]>> => {
  const response = await superAdminApi.get<ApiResponse<ConciergeSlot[]>>(
    `${CONCIERGE_RESOURCE}/slots`
  );

  return response.data;
};

export const publishConciergeSlot = async (
  data: PublishConciergeSlotPayload
): Promise<ApiResponse<ConciergeSlot>> => {
  const response = await superAdminApi.post<ApiResponse<ConciergeSlot>>(
    `${CONCIERGE_RESOURCE}/slots`,
    data
  );

  return response.data;
};

export const getConciergeAppointments = async (): Promise<
  ApiResponse<ConciergeAppointmentItem[]>
> => {
  const response = await superAdminApi.get<ApiResponse<ConciergeAppointmentItem[]>>(
    `${CONCIERGE_RESOURCE}/appointments`
  );

  return response.data;
};

export const completeConciergeBooking = async (
  appointmentId: number,
  payload: CompleteConciergeBookingPayload
): Promise<ApiResponse<ConciergeAppointmentItem>> => {
  const response = await superAdminApi.patch<ApiResponse<ConciergeAppointmentItem>>(
    `${CONCIERGE_RESOURCE}/appointments/${appointmentId}`,
    payload
  );

  return response.data;
};
